import { schemeCategory10 } from "d3-scale-chromatic";
import { shadeColor } from "@helper/colors";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ColoringLevel = "none" | "area" | "tree" | "installation";

export type DataScienceColorState = {
  coloringLevel: ColoringLevel;
  areaColorMap: Record<number | string, string>;
  treeColorMap: Record<number | string, string>;
  installationColorMap: Record<number | string, string>;
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_BASE_COLORS = 6;

const MAX_SHADES_PER_PARENT = 6;

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

/**
 * 6-colour base palette drawn from d3 Category10. Chosen for visual
 * distinctness; if more than MAX_BASE_COLORS parents are selected the
 * palette wraps via getPaletteColor.
 */
function getBasePalette(): readonly string[] {
  return schemeCategory10.slice(0, MAX_BASE_COLORS);
}

function getPaletteColor(index: number, palette: readonly string[]): string {
  return palette[index % palette.length];
}

/**
 * Sort IDs deterministically so the same selection always maps to the same
 * colours regardless of the order items were checked.
 * Numbers sort numerically first, then strings lexicographically.
 */
function getStableSortedIds<T extends number | string>(ids: T[]): T[] {
  return [...ids].sort((a, b) => {
    const aNum = typeof a === "number";
    const bNum = typeof b === "number";
    if (aNum && bNum) return (a as number) - (b as number);
    if (aNum) return -1;
    if (bNum) return 1;
    return String(a).localeCompare(String(b));
  });
}

/**
 * For `count` children under one parent, return an array of shade-percent
 * offsets that alternate lighter / darker around the base colour.
 * Clamped to MAX_SHADES_PER_PARENT — beyond that, offsets repeat.
 *
 * Example for count=4, step=15:  [0, -15, 15, -30]
 */
function getShadeOffsets(count: number, step: number): number[] {
  const effectiveCount = Math.min(count, MAX_SHADES_PER_PARENT);
  const offsets: number[] = [];
  for (let i = 0; i < effectiveCount; i++) {
    const factor = Math.ceil((i + 1) / 2);
    const sign = i % 2 === 0 ? 1 : -1;
    offsets.push(i === 0 ? 0 : sign * factor * step);
  }
  const result: number[] = [];
  for (let i = 0; i < count; i++) {
    result.push(offsets[i % effectiveCount]);
  }
  return result;
}

// ---------------------------------------------------------------------------
// Coloring-level detection (effective hierarchy)
// ---------------------------------------------------------------------------

function computeColoringLevel(
  selectedAreaIds: (number | string)[],
  selectedTreeIds: (number | string)[],
  selectedInstallationIds: (number | string)[]
): ColoringLevel {
  if (selectedAreaIds.length > 1) return "area";
  if (selectedTreeIds.length > 1) return "tree";
  if (selectedInstallationIds.length > 1) return "installation";
  return "none";
}

// ---------------------------------------------------------------------------
// Build functions per level
// ---------------------------------------------------------------------------

function buildAreaModeColors(
  selectedAreaIds: (number | string)[],
  selectedTreeIds: (number | string)[],
  selectedInstallationIds: (number | string)[],
  areas: any[],
  installations: any[],
  palette: readonly string[]
): Pick<DataScienceColorState, "areaColorMap" | "treeColorMap" | "installationColorMap"> {
  const areaColorMap: Record<number | string, string> = {};
  const treeColorMap: Record<number | string, string> = {};
  const installationColorMap: Record<number | string, string> = {};

  const sortedAreaIds = getStableSortedIds(selectedAreaIds);
  const selectedTreeSet = new Set(selectedTreeIds.map(String));
  const selectedInstSet = new Set(selectedInstallationIds.map(String));

  const areaById = new Map<string, any>();
  for (const area of areas) {
    areaById.set(String(area.id), area);
  }

  sortedAreaIds.forEach((areaId, areaIdx) => {
    const baseColor = getPaletteColor(areaIdx, palette);
    areaColorMap[areaId] = baseColor;

    const area = areaById.get(String(areaId));
    if (!area?.tree_set) return;

    const treesInArea = area.tree_set
      .filter((t: any) => selectedTreeSet.has(String(t.id)))
      .map((t: any) => t.id);
    const sortedTreesInArea = getStableSortedIds(treesInArea);

    const treeShadeOffsets = getShadeOffsets(sortedTreesInArea.length, 20);

    sortedTreesInArea.forEach((treeId, treeIdx) => {
      const treeColor =
        sortedTreesInArea.length === 1
          ? baseColor
          : shadeColor(baseColor, treeShadeOffsets[treeIdx]);
      treeColorMap[treeId] = treeColor;

      const instsForTree = installations.filter(
        (inst: any) => inst?.tree?.id != null && String(inst.tree.id) === String(treeId)
      );
      const selectedInstsForTree = instsForTree
        .filter((inst: any) => selectedInstSet.has(String(inst.id)))
        .map((inst: any) => inst.id);

      for (const instId of selectedInstsForTree) {
        installationColorMap[instId] = treeColor;
      }
    });
  });

  return { areaColorMap, treeColorMap, installationColorMap };
}

function buildTreeModeColors(
  selectedTreeIds: (number | string)[],
  selectedInstallationIds: (number | string)[],
  installations: any[],
  palette: readonly string[]
): Pick<DataScienceColorState, "treeColorMap" | "installationColorMap"> {
  const treeColorMap: Record<number | string, string> = {};
  const installationColorMap: Record<number | string, string> = {};

  const sortedTreeIds = getStableSortedIds(selectedTreeIds);
  const selectedInstSet = new Set(selectedInstallationIds.map(String));

  sortedTreeIds.forEach((treeId, treeIdx) => {
    const baseColor = getPaletteColor(treeIdx, palette);
    treeColorMap[treeId] = baseColor;

    const instsForTree = installations
      .filter(
        (inst: any) =>
          inst?.tree?.id != null &&
          String(inst.tree.id) === String(treeId) &&
          selectedInstSet.has(String(inst.id))
      )
      .map((inst: any) => inst.id);
    const sortedInsts = getStableSortedIds(instsForTree);

    const shadeOffsets = getShadeOffsets(sortedInsts.length, 25);

    sortedInsts.forEach((instId, instIdx) => {
      installationColorMap[instId] =
        sortedInsts.length === 1 ? baseColor : shadeColor(baseColor, shadeOffsets[instIdx]);
    });
  });

  return { treeColorMap, installationColorMap };
}

function buildInstallationModeColors(
  selectedInstallationIds: (number | string)[],
  palette: readonly string[]
): Pick<DataScienceColorState, "installationColorMap"> {
  const installationColorMap: Record<number | string, string> = {};

  const sortedIds = getStableSortedIds(selectedInstallationIds);
  sortedIds.forEach((id, idx) => {
    installationColorMap[id] = getPaletteColor(idx, palette);
  });

  return { installationColorMap };
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

const EMPTY_STATE: DataScienceColorState = {
  coloringLevel: "none",
  areaColorMap: {},
  treeColorMap: {},
  installationColorMap: {},
};

/**
 * Build a per-installation color map based on the actual plotted installations.
 * Resolves same-tree collisions: if multiple installations share a tree,
 * they get shaded variants of a base color so they're visually distinct.
 * Single-installation-per-tree preserves the existing hierarchical color.
 */
export function buildPlottedSeriesColorMap(
  plottedInstallations: any[],
  existingColorMap: Record<number | string, string>
): Record<number | string, string> {
  if (!plottedInstallations.length) return {};

  const result: Record<number | string, string> = {};
  const palette = getBasePalette();

  // Group plotted installations by tree.id
  const byTree = new Map<string, any[]>();
  for (const inst of plottedInstallations) {
    const treeKey = String(inst?.tree?.id ?? "unknown");
    if (!byTree.has(treeKey)) byTree.set(treeKey, []);
    byTree.get(treeKey)!.push(inst);
  }

  const treeKeys = [...byTree.keys()];

  // SPECIAL CASE: all plotted installations belong to ONE tree AND there are
  // multiple sensors. Shades of a single base color are too similar — use
  // distinct palette colors instead.
  if (treeKeys.length === 1 && plottedInstallations.length > 1) {
    const insts = byTree.get(treeKeys[0])!;
    const sortedInstIds = getStableSortedIds(insts.map((i: any) => i.id));

    sortedInstIds.forEach((instId: number | string, idx: number) => {
      result[instId] = getPaletteColor(idx, palette);
    });

    return result;
  }

  const sortedTreeKeys = getStableSortedIds(treeKeys);
  let fallbackIdx = 0;

  for (const treeKey of sortedTreeKeys) {
    const insts = byTree.get(treeKey)!;
    const sortedInstIds = getStableSortedIds(insts.map((i: any) => i.id));

    const resolveExistingColor = (instId: number | string): string | undefined =>
      existingColorMap[instId] ?? existingColorMap[String(instId)];

    if (sortedInstIds.length === 1) {
      const instId = sortedInstIds[0];
      result[instId] = resolveExistingColor(instId) || getPaletteColor(fallbackIdx, palette);
      fallbackIdx++;
    } else {
      const baseColor =
        sortedInstIds.reduce<string | undefined>(
          (found, id) => found || resolveExistingColor(id),
          undefined
        ) || getPaletteColor(fallbackIdx, palette);

      const shadeOffsets = getShadeOffsets(sortedInstIds.length, 25);

      sortedInstIds.forEach((instId: number | string, idx: number) => {
        result[instId] = shadeColor(baseColor, shadeOffsets[idx]);
      });
      fallbackIdx++;
    }
  }

  return result;
}

export function computeColorAssignment(
  selectedAreaIds: (number | string)[],
  selectedTreeIds: (number | string)[],
  selectedInstallationIds: (number | string)[],
  areas: any[],
  installations: any[]
): DataScienceColorState {
  const level = computeColoringLevel(selectedAreaIds, selectedTreeIds, selectedInstallationIds);

  if (level === "none") return EMPTY_STATE;

  const palette = getBasePalette();

  if (level === "area") {
    const maps = buildAreaModeColors(
      selectedAreaIds,
      selectedTreeIds,
      selectedInstallationIds,
      areas,
      installations,
      palette
    );
    return { coloringLevel: "area", ...maps };
  }

  if (level === "tree") {
    const { treeColorMap, installationColorMap } = buildTreeModeColors(
      selectedTreeIds,
      selectedInstallationIds,
      installations,
      palette
    );
    return { coloringLevel: "tree", areaColorMap: {}, treeColorMap, installationColorMap };
  }

  const { installationColorMap } = buildInstallationModeColors(selectedInstallationIds, palette);
  return {
    coloringLevel: "installation",
    areaColorMap: {},
    treeColorMap: {},
    installationColorMap,
  };
}
